"use client";

import { useState } from "react";
import useSWR from "swr";
import { fetcher } from "@/lib/api";
import { StockChart, DEFAULT_MA_LINES, type MaLine } from "@/components/trading/stock-chart";
import { MarketSparkline } from "@/components/holding/market-sparkline";
import { usePortfolioContext } from "@/contexts/portfolio-context";
import { useMarketStream } from "@/hooks/use-market-stream";

// 스파크라인 표시 대상 — 국내 계좌: 코스피/코스닥/나스닥선물, 해외 계좌: KORU/SOX/나스닥/S&P500
const SPARK_DOMESTIC = new Set(["KOSPI", "KOSDAQ", "NASDAQ_F"]);
const SPARK_OVERSEAS = new Set(["KORU", "SOX", "NASDAQ", "SP500"]);

// 지표 차트 전용 이평선 — 기본(5/20/60/120)에 200일선(검정) 추가.
// 모듈 상수여야 함 (인라인 배열은 렌더마다 차트 재생성 유발)
const MARKET_MA_LINES: MaLine[] = [
  ...DEFAULT_MA_LINES,
  { period: 200, color: "#111827", lineWidth: 1 },
];
// FDR 은 분봉 미지원 — 5분 버튼 제외
const MARKET_PERIODS = ["D", "W", "M"] as const;

interface MarketData {
  price: number;
  change_rate: number;
}

interface MarketResponse {
  success: boolean;
  data: Record<string, MarketData>;
  updated_at: string | null;
}

const MARKET_LABELS: Record<string, string> = {
  USDKRW: "환율",
  KOSPI: "코스피",
  KOSDAQ: "코스닥",
  KORU: "KORU",
  SOX: "SOX",
  NASDAQ_F: "나스닥선물",
  NASDAQ: "나스닥",
  SP500: "S&P500",
  US10Y: "미국채10",
  GOLD: "금",
  COPPER: "구리",
  WTI: "WTI유",
  BTC: "BTC",
};

// 네이버 증권 차트 링크 — 라벨 클릭 시 새 탭으로 이동 (SOX 만 investing.com)
const MARKET_LINKS: Record<string, string> = {
  USDKRW:   "https://stock.naver.com/market/marketindex/major",
  KOSPI:    "https://stock.naver.com/market/stock/kr",
  KOSDAQ:   "https://stock.naver.com/market/stock/kr",
  KORU:     "https://stock.naver.com/worldstock/etf/KORU.K/price",
  NASDAQ_F: "https://stock.naver.com/market/stock/usa",
  SOX:      "https://kr.investing.com/indices/phlx-semiconductor",
  NASDAQ:   "https://stock.naver.com/market/stock/usa",
  SP500:    "https://stock.naver.com/market/stock/usa",
  US10Y:    "https://stock.naver.com/market/marketindex/major",
  GOLD:     "https://stock.naver.com/market/marketindex/major",
  COPPER:   "https://stock.naver.com/market/marketindex/major",
  WTI:      "https://stock.naver.com/market/marketindex/major",
  BTC:      "https://stock.naver.com/market/crypto",
  ETH:      "https://stock.naver.com/market/crypto",
};

export function MarketIndices() {
  const { currency } = usePortfolioContext();
  const sparkSet = currency === "USD" ? SPARK_OVERSEAS : SPARK_DOMESTIC;
  const { data, isLoading } = useSWR<MarketResponse>(
    "/api/market",
    fetcher,
    { refreshInterval: 10000 }  // 10s — KOSPI/KOSDAQ KIS 실시간 시세 갱신
  );
  // 박스 클릭 시 드랍다운 차트 (라벨 링크 클릭은 제외) — FDR 소스, 종목 차트와 동일 UI
  const [chartKey, setChartKey] = useState<string | null>(null);

  if (isLoading || !data?.data) {
    return (
      <div className="flex gap-3 overflow-x-auto pb-1">
        {Object.keys(MARKET_LABELS).map((key) => (
          <div key={key} className="flex-shrink-0 px-3 py-1.5 rounded-full animate-pulse" style={{ background: "#ffffff" }}>
            <div className="h-3 w-14 bg-black/10 rounded" />
          </div>
        ))}
      </div>
    );
  }

  const markets = data.data;

  return (
    <>
    <div className="flex gap-2 overflow-x-auto pb-1">
      {Object.entries(MARKET_LABELS).map(([key, label]) => {
        const market = markets[key];
        if (!market) return null;

        const isPositive = market.change_rate >= 0;
        const changePercent = (market.change_rate * 100).toFixed(2);

        // 강조(바탕색+흰 글씨) 임계 — 일반 지수는 ±1%, KORU 는 ±10% (그 안에서는 흰 바탕+글자색).
        // US10Y 는 금리값 표시라 제외. 앱 관례: 상승=빨강, 하락=초록.
        const isPctItem = key !== "US10Y";
        const pct = market.change_rate * 100;
        const thresh = key === "KORU" ? 10 : 1;
        const strong = isPctItem && pct > thresh ? "up" : isPctItem && pct < -thresh ? "down" : null;
        const chipBg = strong === "up" ? "#ff6b63" : strong === "down" ? "#2faa5c" : "#ffffff";
        const labelClass = strong
          ? "text-white/90 hover:text-white"
          : "text-black/65 hover:text-black/80";
        const valueClass = strong ? "text-white" : isPositive ? "text-gain" : "text-loss";
        const subClass = strong ? "text-white/75" : "text-black/45";

        return (
          <div
            key={key}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1 rounded-full cursor-pointer"
            style={{ background: chipBg, outline: chartKey === key ? "2px solid rgba(59,130,246,0.6)" : undefined }}
            onClick={() => setChartKey((v) => (v === key ? null : key))}
            title={`${label} 차트 펼치기`}
          >
            {MARKET_LINKS[key] ? (
              <a
                href={MARKET_LINKS[key]}
                target="_blank"
                rel="noopener noreferrer"
                className={`text-xs ${labelClass} hover:underline tracking-[-0.08px] transition-colors`}
                title={`${label} 차트 (외부 링크)`}
                onClick={(e) => e.stopPropagation()}
              >
                {label}
              </a>
            ) : (
              <span className={`text-xs ${labelClass} tracking-[-0.08px]`}>
                {label}
              </span>
            )}
            {key === "US10Y" ? (
              <>
                <span className={`text-xs font-medium tracking-[-0.08px] ${valueClass}`}>
                  {isPositive ? "+" : ""}{changePercent}%
                </span>
                <span className={`text-xs ${subClass} tracking-[-0.08px]`}>
                  ({market.price.toFixed(2)})
                </span>
              </>
            ) : (
              <>
                <span className={`text-xs font-medium tracking-[-0.08px] ${valueClass}`}>
                  {isPositive ? "+" : ""}{changePercent}%
                </span>
                {["USDKRW", "KOSPI", "KOSDAQ", "NASDAQ", "SP500", "WTI"].includes(key) && (
                  <span className={`text-xs ${subClass} tracking-[-0.08px]`}>
                    ({market.price.toLocaleString(undefined, { maximumFractionDigits: 1 })})
                  </span>
                )}
              </>
            )}
            {/* 장중 스파크라인 — 계좌 종류에 따라 대상 지표만 표시 */}
            <MarketSparkline symbol={key} isPositive={isPositive} strong={!!strong} enabled={sparkSet.has(key)} />
          </div>
        );
      })}
    </div>
    {/* 드랍다운 차트 — 바깥 클릭 시 닫힘 (backdrop) */}
    {chartKey && (
      <>
        <div className="fixed inset-0 z-40" onClick={() => setChartKey(null)} />
        <div className="relative z-50">
          <div
            className="absolute left-0 right-0 top-0 h-[440px] rounded-lg border border-black/10 shadow-2xl overflow-hidden"
            style={{ background: "#ffffff" }}
          >
            <StockChart
              key={chartKey}
              portfolioName=""
              stockCode={chartKey}
              stockName={MARKET_LABELS[chartKey] || chartKey}
              chartUrlBase={`/api/market/chart?symbol=${encodeURIComponent(chartKey)}`}
              maLines={MARKET_MA_LINES}
              periods={MARKET_PERIODS}
              enableVwap
            />
          </div>
        </div>
      </>
    )}
    </>
  );
}
